import React, { ReactElement, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Offcanvas from "react-bootstrap/Offcanvas";

import { RiCloseLine } from "@remixicon/react";
import { DashboardTicketDisplay } from "../../../__generated__/api";
import useKeyboardShortcuts, { useKeyboardState } from "../../../contexts/KeyboardShortcutContext";
import useRouting, { useParams } from "../../../contexts/Routing";
import { useTicketState } from "../../../contexts/ticket-state";
import ChatView from "./ChatView";
import Styles from "./Chat.module.scss";
import NoResults from "../board/no-results/NoResults";
import usePortal from "../../../contexts/PortalContext";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";

export function ChatError(): ReactElement {
    const { createHistoryPush, Paths } = useRouting();

    return (
        <div className={Styles.chat}>
            <NoResults
                onClear={createHistoryPush(Paths.AGENT)}
                title="Conversation Not Found"
                text="This conversation could not be found or you may not have the correct permissions."
                resetButtonText="Close"
                resetIcon={<RiCloseLine />}
            />
        </div>
    );
}

const KEYBOARD_CONTEXT = "chat";
export default function Chat(): ReactElement {
    const { Paths, historyPushWithSearch, getSearchParams } = useRouting();
    const { chatId } = useParams(Paths.AGENT_CHAT);
    const { setTitle } = usePortal();

    const ticketState = useTicketState();
    const [isShow, setIsShow] = useState(false);

    const keyboardState = useKeyboardState();
    const { ticket, error, isNew } = ticketState.tickets.useGetOrFetch(chatId);

    useEffect(() => {
        if (!isNew) {
            if (isShow && ticket) {
                setTimeout(() => {
                    ticket.scrollIntoViewWithUpdate();
                }, 300);
                setTitle(`${ticket?.chat.ticketNumber} - ${ticket?.chat.subject?.createBlurbSingleLine(30)}`);
            } else if (!isShow) {
                setTitle("");
            }
            ticketState.selectionState.setOpen(isShow ? ticket : undefined);
        }
        return () => {
            setTitle("");
        };
    }, [ticket, isShow, isNew]);

    const display = ticketState.currentDashboard?.settings?.ticketDisplay ?? DashboardTicketDisplay.SideRight;

    useEffect(() => {
        if (!isNew && chatId && (ticket || error)) {
            setIsShow(true);
            keyboardState.pushContext(KEYBOARD_CONTEXT);
        } else {
            setIsShow(false);
            keyboardState.popContext(KEYBOARD_CONTEXT);
        }
    }, [chatId, ticket, error, isNew]);

    const hide = () => {
        setIsShow(false);
        historyPushWithSearch(Paths.AGENT, getSearchParams(Paths.AGENT));
    };

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "esc",
            shouldUseAllContexts: true,
            context: KEYBOARD_CONTEXT,
            handler: () => {
                hide();
            },
        });
    });

    const chatDisplay = error ? (
        <ChatError />
    ) : (
        <ChatView
            ticket={ticket}
            onClose={hide}
            key={ticket?.id}
            metricPathSection={LoggingHandler.Metrics.AGENT}
            displayType={display}
            onToggleDisplay={() => {
                ticketState.currentDashboardSettings = {
                    ...ticketState.currentDashboardSettings,
                    ticketDisplay: display === DashboardTicketDisplay.Modal
                        ? DashboardTicketDisplay.SideRight
                        : DashboardTicketDisplay.Modal,
                };
            }}
        />
    );
    if (display === DashboardTicketDisplay.Modal) {
        return (
            <Modal
                show={isShow}
                size="lg"
                className={Styles["chat-modal"]}
                enforceFocus={false}
            >
                <Modal.Body>
                    {chatDisplay}
                </Modal.Body>
            </Modal>
        );
    }
    return (
        <Offcanvas
            show={isShow}
            className={Styles["chat-offcanvas"]}
            scroll
            backdrop={false}
            onHide={hide}
            placement="end"
            keyboard={false}
        >
            <Offcanvas.Body>
                {chatDisplay}
            </Offcanvas.Body>
        </Offcanvas>
    );
}
